import React, { useState, useCallback, useEffect } from 'react';
import { useSlate } from 'slate-react';
import { Editor, Transforms, Range, Element as SlateElement } from 'slate';

const LIST_TYPES = ['numbered-list', 'bulleted-list'];

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const isBlockActive = (editor, format) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.type === format,
    })
  );

  return !!match;
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type),
    split: true,
  });

  Transforms.setNodes(editor, {
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  });

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

/* ---- Link helpers ---- */

const isLinkActive = (editor) => {
  const [link] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
  return !!link;
};

const unwrapLink = (editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

const wrapLink = (editor, url) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

/* ---- Image helper ---- */

const insertImage = (editor, url, alt) => {
  const image = { type: 'image', url, alt: alt || '', children: [{ text: '' }] };
  Transforms.insertNodes(editor, image);
  // Insert a paragraph after the image so user can continue typing
  Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] });
};

/* ---- Table helper ---- */

const insertTable = (editor, rows, cols) => {
  const tableRows = [];
  for (let r = 0; r < rows; r++) {
    const cells = [];
    for (let c = 0; c < cols; c++) {
      cells.push({
        type: 'table-cell',
        children: [{ text: r === 0 ? `Header ${c + 1}` : '' }],
      });
    }
    tableRows.push({ type: 'table-row', children: cells });
  }
  const table = { type: 'table', children: tableRows };
  Transforms.insertNodes(editor, table);
  // Insert a paragraph after the table
  Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] });
};

/* ---- Toolbar buttons ---- */

const MarkButton = ({ format, label, title }) => {
  const editor = useSlate();
  const active = isMarkActive(editor, format);
  return (
    <button
      className={active ? 'active' : ''}
      onMouseDown={(e) => {
        e.preventDefault();
        toggleMark(editor, format);
      }}
      title={title || label}
    >
      {label}
    </button>
  );
};

const BlockButton = ({ format, label, title }) => {
  const editor = useSlate();
  const active = isBlockActive(editor, format);
  return (
    <button
      className={active ? 'active' : ''}
      onMouseDown={(e) => {
        e.preventDefault();
        toggleBlock(editor, format);
      }}
      title={title || label}
    >
      {label}
    </button>
  );
};

const Separator = () => <div className="separator" />;

/* ---- Popup component for inputs ---- */
const ToolbarPopup = ({ visible, onClose, onSubmit, fields, title }) => {
  const [values, setValues] = useState({});

  useEffect(() => {
    if (visible) {
      const defaults = {};
      fields.forEach((f) => { defaults[f.name] = f.defaultValue || ''; });
      setValues(defaults);
    }
  }, [visible, fields]);

  if (!visible) return null;

  return (
    <div className="slate-toolbar-popup-overlay" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <div className="slate-toolbar-popup">
        <div className="slate-toolbar-popup-title">{title}</div>
        {fields.map((field) => (
          <div key={field.name} className="slate-toolbar-popup-field">
            <label>{field.label}</label>
            <input
              type={field.type || 'text'}
              value={values[field.name] || ''}
              placeholder={field.placeholder || ''}
              onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSubmit(values);
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onClose();
                }
              }}
              autoFocus={field === fields[0]}
            />
          </div>
        ))}
        <div className="slate-toolbar-popup-actions">
          <button className="slate-popup-btn cancel" onMouseDown={(e) => { e.preventDefault(); onClose(); }}>Cancel</button>
          <button className="slate-popup-btn confirm" onMouseDown={(e) => { e.preventDefault(); onSubmit(values); }}>OK</button>
        </div>
      </div>
    </div>
  );
};

/* ---- Main Toolbar ---- */

const SlateToolbar = ({ isFullscreen, onToggleFullscreen }) => {
  const editor = useSlate();
  const [linkPopup, setLinkPopup] = useState(false);
  const [imagePopup, setImagePopup] = useState(false);
  const [tablePopup, setTablePopup] = useState(false);

  // Listen for Ctrl+K shortcut from SlateEditor
  useEffect(() => {
    const handler = () => setLinkPopup(true);
    window.addEventListener('slate-insert-link', handler);
    return () => window.removeEventListener('slate-insert-link', handler);
  }, []);

  const handleLinkSubmit = useCallback((values) => {
    const url = values.url?.trim();
    if (url) {
      wrapLink(editor, url);
    }
    setLinkPopup(false);
  }, [editor]);

  const handleImageSubmit = useCallback((values) => {
    const url = values.url?.trim();
    if (url) {
      insertImage(editor, url, values.alt?.trim());
    }
    setImagePopup(false);
  }, [editor]);

  const handleTableSubmit = useCallback((values) => {
    const rows = Math.max(1, Math.min(10, parseInt(values.rows, 10) || 2));
    const cols = Math.max(1, Math.min(10, parseInt(values.cols, 10) || 3));
    insertTable(editor, rows, cols);
    setTablePopup(false);
  }, [editor]);

  const linkActive = isLinkActive(editor);

  return (
    <div className="slate-editor-toolbar" style={{ position: 'relative' }}>
      <MarkButton format="bold" label="B" title="Bold (Ctrl+B)" />
      <MarkButton format="italic" label="I" title="Italic (Ctrl+I)" />
      <MarkButton format="code" label="</>" title="Code (Ctrl+`)" />
      <Separator />
      <BlockButton format="heading-one" label="H1" title="Heading 1" />
      <BlockButton format="heading-two" label="H2" title="Heading 2" />
      <Separator />
      <BlockButton format="bulleted-list" label="UL" title="Bullet List" />
      <BlockButton format="numbered-list" label="OL" title="Numbered List" />
      <BlockButton format="block-quote" label={'\u201D'} title="Blockquote" />
      <Separator />
      {/* Link button */}
      <button
        className={linkActive ? 'active' : ''}
        onMouseDown={(e) => {
          e.preventDefault();
          if (linkActive) {
            unwrapLink(editor);
          } else {
            setLinkPopup(true);
          }
        }}
        title="Link (Ctrl+K)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      </button>
      {/* Image button */}
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          setImagePopup(true);
        }}
        title="Insert Image"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      </button>
      {/* Table button */}
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          setTablePopup(true);
        }}
        title="Insert Table"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="3" y1="15" x2="21" y2="15" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="15" y1="3" x2="15" y2="21" />
        </svg>
      </button>

      {/* Fullscreen toggle */}
      <div className="separator" />
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          if (onToggleFullscreen) onToggleFullscreen();
        }}
        title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen Editor'}
        className={isFullscreen ? 'active' : ''}
      >
        {isFullscreen ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 14 10 14 10 20" />
            <polyline points="20 10 14 10 14 4" />
            <line x1="14" y1="10" x2="21" y2="3" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        )}
      </button>

      {/* Popups */}
      <ToolbarPopup
        visible={linkPopup}
        onClose={() => setLinkPopup(false)}
        onSubmit={handleLinkSubmit}
        title="Insert Link"
        fields={[
          { name: 'url', label: 'URL', placeholder: 'https://example.com' },
        ]}
      />
      <ToolbarPopup
        visible={imagePopup}
        onClose={() => setImagePopup(false)}
        onSubmit={handleImageSubmit}
        title="Insert Image"
        fields={[
          { name: 'url', label: 'Image URL', placeholder: 'https://example.com/image.png' },
          { name: 'alt', label: 'Alt Text', placeholder: 'Description (optional)' },
        ]}
      />
      <ToolbarPopup
        visible={tablePopup}
        onClose={() => setTablePopup(false)}
        onSubmit={handleTableSubmit}
        title="Insert Table"
        fields={[
          { name: 'rows', label: 'Rows', placeholder: '3', type: 'number', defaultValue: '3' },
          { name: 'cols', label: 'Columns', placeholder: '3', type: 'number', defaultValue: '3' },
        ]}
      />
    </div>
  );
};

export { SlateToolbar, toggleMark };
